/**
 * @module Utils/General
 * @description This file contains general functions for the project
 * 
 */


/**
 * @function
 * @description The function returns a CSS value for a given aspect ratio.
 * @param {string} [ratio=1/1] - The ratio parameter is a string that represents the aspect ratio of an element.
 * The default value is '1/1', which means a square aspect ratio. Other possible values include '4/3',
 * '3/4', '6/4', '4/6', '16/9',
 * @returns {string} The function `getRatio` returns a string value that represents the CSS `calc()` function
 * for a given aspect ratio. The aspect ratio is passed as an argument to the function, and if no
 * argument is provided, the default aspect ratio of `1/1` is used. The returned value is determined by
 * looking up the aspect ratio in an object that maps aspect ratios to their corresponding `calc()`
 */
export const getRatio = (ratio = '1/1') => {
    return {
      '4/3': 'calc(100% / 4 * 3)',
      '3/4': 'calc(100% / 3 * 4)',
      '6/4': 'calc(100% / 6 * 4)',
      '4/6': 'calc(100% / 4 * 6)',
      '16/9': 'calc(100% / 16 * 9)',
      '9/16': 'calc(100% / 9 * 16)',
      '21/9': 'calc(100% / 21 * 9)',
      '9/21': 'calc(100% / 9 * 21)',
      '1/1': '100%',
    }[ratio];
}

/**
 * @function
 * @description This function updates the authorization header of a server with a bearer token retrieved from local
 * storage.
 * @param server - The `server` parameter is likely an instance of Axios, a popular JavaScript library
 * used for making HTTP requests. This function is updating the default headers of the Axios instance
 * to include an Authorization header with a bearer token retrieved from the `localStorage`.
 * @returns The function is not returning anything explicitly, but it may return undefined if the
 * condition in the if statement is true and the function exits early.
 */
export const updateAuthorizationHeader = (server) => {
  const logData = localStorage.getItem('session');
  if(!logData){
      console.log('Token not valid')
      return
  }
  server.defaults.headers.common['Authorization'] = `Bearer ${logData}`;
}

/**
 * @function
 * @description This function removes the Authorization header from the common headers of a server object in
 * JavaScript.
 * @param server - The server parameter is likely an instance of Axios, a popular JavaScript library
 * used for making HTTP requests. This function removes the Authorization header from the default
 * headers of the Axios instance, which means that subsequent requests made using that instance will
 * not include an Authorization header unless it is explicitly added again.
 */
export const removeAuthorizationHeader = (server) => {
  delete server.defaults.headers.common.Authorization;
}

/**
 * @function
 * @description The function `sleep` returns a promise that resolves after a specified amount of time has passed.
 * @param ms - The parameter `ms` is a number representing the duration of time in milliseconds for
 * which the function `sleep` will pause the execution of the code. The function returns a Promise that
 * resolves after the specified time has elapsed.
 * @returns A Promise object is being returned. The Promise will resolve after the specified number of
 * milliseconds (ms) have elapsed.
 */
export const sleep = (ms) => {
  return new Promise(resolve => setTimeout(resolve, ms));
}